import React, { useState } from "react";
import { useLocation, useParams } from "react-router";
import { toast } from "sonner";
import { AuditLogQueryParams } from "../repositories/AuditLogsRepository";
import {AuditLogPerformerType, AuditLogPerformerTypeMultipleSelectOptions, AuditLogType, EVENT_TYPE_GROUPS, ALL_EVENT_TYPES, OpenAuditLogState} from "../models/AuditLog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { DateTimeRangePicker, type DateTimeRange } from "@/components/ui/date-time-range-picker";
import EventTypeGroupChip from "../components/AuditLogs/EventTypeGroupChip";
import GenericMultipleSelect, {MultipleSelectOption} from "../components/common/GenericMultipleSelect";
import {formatDate, formatTime} from "../helpers/CommonHelpers";
import AuditLogTable from "../components/AuditLogs/AuditLogTable";
import { fileFiltersBoxClassName, filterComboboxClassName } from "../components/common/Table/TableStyles";
import DocsHelpLink from "@/components/Docs/DocsHelpLink";

interface AuditLogsPageProps {
  /**
   * The viewer may open Resolve Conflicts by Rule in this organization (Admin or Editor, with document access), so a
   * rule run's entry offers to load its rule there and to download its CSV.
   */
  canUseConflictRules?: boolean;
}

const AuditLogsPage: React.FC<AuditLogsPageProps> = ({ canUseConflictRules = false }) => {
  const { organizationId } = useParams();
  const location = useLocation();
  // A link from another page can open the log on its own entry, such as a conflict rule run's.
  const [opening] = useState(() => {
    const state = location.state as Partial<OpenAuditLogState> | null;
    const eventTypes = (state?.eventTypes ?? []).filter((type) => ALL_EVENT_TYPES.includes(type));
    return { eventTypes: eventTypes.length > 0 ? eventTypes : ALL_EVENT_TYPES, auditLogId: state?.auditLogId };
  });

  const [selectedEventTypes, setSelectedEventTypes] = useState<Set<AuditLogType>>(
    new Set(opening.eventTypes)
  );
  const [selectedPerformerTypes, setSelectedPerformerTypes] = useState<MultipleSelectOption[]>(AuditLogPerformerTypeMultipleSelectOptions);

  const now = new Date();
  const today = new Date(now);
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 7);
  yesterday.setMinutes(yesterday.getMinutes() + 1);
  today.setMinutes(today.getMinutes() + 1);

  const [queryParams, setQueryParams] = useState<AuditLogQueryParams>({
    eventTypes: [...opening.eventTypes],
    fromDate: formatDate(yesterday),
    toDate: formatDate(today),
    fromTime: formatTime(yesterday),
    toTime: formatTime(today),
    performerTypes: [AuditLogPerformerType.AssignedUser, AuditLogPerformerType.ExternalUser, AuditLogPerformerType.Admin, AuditLogPerformerType.Client],
  });

  const [shouldFetchAuditLogs, setShouldFetchAuditLogs] = useState(true);

  const handlePerformerTypesChange = (values: MultipleSelectOption[]) => {
    setSelectedPerformerTypes(values);
    const performerTypes = values.map(option => option.id as AuditLogPerformerType);
    setQueryParams((prev) => ({ ...prev, performerTypes }));
  };

  const toggleEventType = (type: AuditLogType) => {
    setSelectedEventTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const toggleGroup = (groupTypes: AuditLogType[]) => {
    setSelectedEventTypes((prev) => {
      const next = new Set(prev);
      const allSelected = groupTypes.every((t) => next.has(t));
      if (allSelected) {
        groupTypes.forEach((t) => next.delete(t));
      } else {
        groupTypes.forEach((t) => next.add(t));
      }
      return next;
    });
  };

  const handleResetToDefault = () => {
    setSelectedEventTypes(new Set(ALL_EVENT_TYPES));
    setSelectedPerformerTypes(AuditLogPerformerTypeMultipleSelectOptions);

    const now = new Date();
    const today = new Date(now);
    const yesterday = new Date(now);
    yesterday.setUTCDate(yesterday.getUTCDate() - 7);

    setQueryParams((prevState) => ({
      ...prevState,
      eventTypes: [...ALL_EVENT_TYPES],
      fromDate: formatDate(yesterday),
      toDate: formatDate(today),
      fromTime: formatTime(yesterday),
      toTime: formatTime(today),
      performerTypes: [
        AuditLogPerformerType.AssignedUser,
        AuditLogPerformerType.ExternalUser,
        AuditLogPerformerType.Admin,
        AuditLogPerformerType.Client,
      ],
    }));
  };

  const handleDeselectAll = () => {
    setSelectedEventTypes(new Set());
    setQueryParams((prevState) => ({ ...prevState, eventTypes: [] }));
  };

  const handleSubmit = () => {
    if (selectedEventTypes.size === 0) {
      setShouldFetchAuditLogs(false);
      toast.error("At least one selected event type filter is required to search");
      return;
    }
    if (!queryParams.fromDate) {
      toast.error("Start date is required");
      return;
    }
    if (!queryParams.toDate) {
      toast.error("End date is required");
      return;
    }

    setShouldFetchAuditLogs(true);
    const eventTypes = Array.from(selectedEventTypes);
    const performerTypes = selectedPerformerTypes.map(option => option.id as AuditLogPerformerType);
    setQueryParams((prev) => ({ ...prev, eventTypes, performerTypes }));
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex mt-2 mb-2 gap-2 flex-col sm:flex-row items-start sm:items-center flex-wrap justify-between shrink-0">
        <h2 className="text-3xl font-bold tracking-tight flex items-center gap-2">Audit Logs<DocsHelpLink to="administration/audit-log" /></h2>
        <div className="flex flex-row gap-2">
          <Button size="sm" variant="secondary" onClick={() => handleDeselectAll()}>Deselect All</Button>
          <Button size="sm" variant="secondary" onClick={() => handleResetToDefault()}>Reset To Default</Button>
          <Button size="sm" onClick={() => handleSubmit()}>Submit</Button>
        </div>
      </div>

      <div className={fileFiltersBoxClassName}>
        <div className={filterComboboxClassName}>
          <Label className="text-xs font-semibold text-muted-foreground">Performer Types</Label>
          <GenericMultipleSelect
            options={AuditLogPerformerTypeMultipleSelectOptions}
            placeholder="Choose performer types"
            inputValues={selectedPerformerTypes}
            setValues={handlePerformerTypesChange}
          />
        </div>
        <div className="flex-1 min-w-[320px] [&>label]:mb-0.5">
          <Label className="text-xs font-semibold text-muted-foreground">Date Range</Label>
          <DateTimeRangePicker
            className="w-full"
            value={{
              fromDate: queryParams.fromDate ?? "",
              fromTime: queryParams.fromTime ?? "",
              toDate: queryParams.toDate ?? "",
              toTime: queryParams.toTime ?? "",
            }}
            onChange={(range: DateTimeRange) =>
              setQueryParams((prev) => ({
                ...prev,
                fromDate: range.fromDate,
                fromTime: range.fromTime,
                toDate: range.toDate,
                toTime: range.toTime,
              }))
            }
          />
        </div>
      </div>

      <div className={fileFiltersBoxClassName}>
        <div className="flex-1">
          <Label className="text-xs font-semibold text-muted-foreground">Event Type</Label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {EVENT_TYPE_GROUPS.map((group) => (
              <EventTypeGroupChip
                key={group.label}
                group={group}
                selectedEventTypes={selectedEventTypes}
                onToggleGroup={toggleGroup}
                onToggleType={toggleEventType}
              />
            ))}
          </div>
        </div>
      </div>

      <AuditLogTable
        organizationId={organizationId ?? ""}
        queryParams={queryParams}
        shouldFetchAuditLogs={shouldFetchAuditLogs}
        canUseConflictRules={canUseConflictRules}
        openAuditLogId={opening.auditLogId}
      />
    </div>
  );
};

export default AuditLogsPage;
